import type { TFunction } from "i18next";

type TipAttributeInfo = {
  display_name: string;
  list_id?: string | null;
};

type TipAttributes = {
  [key: string]: TipAttributeInfo;
};

type TipStageValidation = {
  eligible: boolean;
  blocked_reasons: string[];
  error_code?: string | null;
  required_attributes?: Array<{ key: string; display_name: string }>;
  required_tables?: Array<{ key: string; display_name: string }>;
};

export function formatAttributeList(names: string[]): string {
  if (names.length === 0) return "";
  if (names.length === 1) return names[0];
  if (names.length === 2) return `${names[0]} and ${names[1]}`;
  return `${names.slice(0, -1).join(", ")} and ${names[names.length - 1]}`;
}

export function resolveRightsTableName(
  stageValidation: TipStageValidation,
  t: TFunction
): string {
  const fromTables = (stageValidation.required_tables ?? [])
    .map((table) => table.display_name)
    .filter(Boolean);
  if (fromTables.length > 0) {
    return formatAttributeList(fromTables);
  }

  return t("required rights matching this SKU");
}

export function buildStageValidationTip(
  t: TFunction,
  stageValidation: TipStageValidation,
  attributes?: TipAttributes
): string | undefined {
  if (stageValidation.eligible) {
    return undefined;
  }

  const rightsName = resolveRightsTableName(stageValidation, t);

  const names = (stageValidation.required_attributes ?? [])
    .map((attr) => {
      const fromPayload = attributes?.[attr.key]?.display_name;
      return fromPayload || attr.display_name;
    })
    .filter((name): name is string => Boolean(name));

  if (names.length === 0) {
    return t("To approve, a {{rights}} entry must exist matching this SKU", {
      rights: rightsName,
    });
  }

  return t(
    "To approve, a {{rights}} entry must exist matching this SKU's {{fields}}",
    {
      rights: rightsName,
      fields: formatAttributeList(names),
    }
  );
}
